// BCP作成ページ

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BCP_SECTIONS } from '../config/bcpSections';
import type { BCPFormData, BCPSection } from '../types';
import { useBCPPageContext } from '../hooks/useBCPPageContext';
import { useBCPGeneration } from '../hooks/useBCPGeneration';
import { useBCPLoader } from '../hooks/useBCPLoader';
import { useBCPFormState } from '../hooks/useBCPFormState';
import { useBCPActions } from '../hooks/useBCPActions';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';
import AIAssistant from '../components/AIAssistant';
import BCPGuideSection from '../components/BCPGuideSection';
import BCPPageTitle from '../components/BCPPageTitle';
import GuideTopAI from '../components/GuideTopAI';
import AISuggestOffFormTextarea from '../components/AISuggestOffFormTextarea';
import SectionHeader from '../components/SectionHeader';
import {
  BCPFormLayout,
  ReferenceMaterialsPanel,
} from '../components/BCP';
import BCPDownloadModal from '../components/BCPDownloadModal';

// AI相談ボタンを表示するフィールドID
const AI_CONSULT_FIELD_IDS = [
  'continuityPolicy',
  'riskAnalysis',
  'criticalBusinesses',
  'recoveryPlan',
  'financialPlan',
  'stockpilePlan',
  'alternativeMeasures',
  'activationCriteria',
  'emergencyOrganization',
  'rolesResponsibilities',
  'communicationMethods',
  'regularTraining',
  'bcpReview',
  'employeeEducation'
];

// 大項目ごとにセクションをグループ化
interface BCPSectionGroup {
  id: string;
  title: string;
  fields: BCPSection[];
}

// minHeightの値を実際のpx値にマッピングする関数
const getTextareaMinHeight = (minHeight?: 'small' | 'medium' | 'large'): string => {
  const heightMap = {
    small: '72px',
    medium: '96px',
    large: '300px',
  };
  return heightMap[minHeight || 'medium'];
};

const BCP_SECTION_GROUPS: BCPSectionGroup[] = [
  {
    id: 'basic_info',
    title: '1. 基本情報と事業継続方針の策定',
    fields: BCP_SECTIONS.filter(s => ['companyInfo', 'continuityPolicy'].includes(s.id))
  },
  {
    id: 'risk_analysis',
    title: '2. 想定リスクと影響分析',
    fields: BCP_SECTIONS.filter(s => s.id === 'riskAnalysis')
  },
  {
    id: 'recovery_plan',
    title: '3. 重要事業と復旧計画',
    fields: BCP_SECTIONS.filter(s => ['criticalBusinesses', 'recoveryPlan', 'financialPlan', 'stockpilePlan'].includes(s.id))
  },
  {
    id: 'alternative_measures',
    title: '4. 現地復旧以外の代替手段',
    fields: BCP_SECTIONS.filter(s => s.id === 'alternativeMeasures')
  },
  {
    id: 'activation_criteria',
    title: '5. BCP発動基準',
    fields: BCP_SECTIONS.filter(s => s.id === 'activationCriteria')
  },
  {
    id: 'emergency_organization',
    title: '6. 緊急時の組織体制',
    fields: BCP_SECTIONS.filter(s => ['emergencyOrganization', 'rolesResponsibilities', 'communicationMethods'].includes(s.id))
  },
  {
    id: 'training_review',
    title: '7. 事業継続に向けたその他の取組み',
    fields: BCP_SECTIONS.filter(s => ['regularTraining', 'bcpReview', 'employeeEducation'].includes(s.id))
  }
];


const BCPCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // AI相談ボタン用の状態
  const [selectedFieldId, setSelectedFieldId] = React.useState<string | null>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(2);
  const [savedBcpId, setSavedBcpId] = React.useState<string | null>(null);
  const openModalParam = searchParams.get('openModal');

  // 離脱防止フラグ
  // - isSavingOrDownloading: 保存・ダウンロード処理中
  // - hasSaved: 保存成功後 (TOPへ戻る際は確認不要)
  const [isSavingOrDownloading, setIsSavingOrDownloading] = React.useState(false);
  const [hasSaved, setHasSaved] = React.useState(false);

  // Get page context (URL params, navigation state, etc.)
  const pageContext = useBCPPageContext();
  const {
    bcpId,
    tenantId,
    uploadedFiles,
    apiResponse,
    bcpType,
    companyInfo,
    restorePolicyData,
    isEditMode,
    hasUploadedFiles
  } = pageContext;

  // Load BCP document for edit mode
  const {
    loading: bcpLoading,
    bcpDocument,
    getInitialFormData
  } = useBCPLoader({
    bcpId,
    tenantId,
  });

  // Generate BCP from uploaded files (only if not in edit mode)
  const {
    isLoading: generationLoading,
    formData: generatedFormData,
    hasGenerated,
    error: generationError,
    regenerate
  } = useBCPGeneration({
    isEditMode,
    uploadedFiles,
    apiResponse,
    companyInfo,
    restorePolicyData
  });

  // Initialize form state
  const initialFormData = isEditMode ? getInitialFormData() : generatedFormData;
  const formState = useBCPFormState({
    initialData: initialFormData,
    sections: BCP_SECTIONS
  });

  // Handle BCP actions (save, export, etc.)
  const actions = useBCPActions({
    isEditMode,
    bcpDocument,
    bcpType,
    tenantId,
    apiResponse,
    onRegenerate: regenerate
  });

  // 離脱防止を有効化
  // 保存成功後またはダウンロード処理中は確認をスキップ
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => isSavingOrDownloading || hasSaved,
  });

  // Effect to sync generated data to form when generation completes
  React.useEffect(() => {
    if (hasGenerated && generatedFormData && !isEditMode) {
      formState.resetForm(generatedFormData);
    }
  }, [hasGenerated, generatedFormData, isEditMode]);

  // Effect to sync BCP document data when editing
  React.useEffect(() => {
    if (isEditMode && bcpDocument) {
      const editFormData = getInitialFormData();
      formState.resetForm(editFormData);
    }
  }, [isEditMode, bcpDocument]);

  // Effect to open modal when redirected after save (only for edit mode with openModal param)
  React.useEffect(() => {
    if (openModalParam === 'true' && bcpId && isEditMode) {
      setCurrentStep(3);
      setIsDownloadModalOpen(true);

      // Remove openModal param from URL
      const params = new URLSearchParams(window.location.search);
      params.delete('openModal');
      const newSearch = params.toString();
      const newUrl = newSearch ? `/bcp-create?${newSearch}` : '/bcp-create';
      window.history.replaceState({}, '', newUrl);
    }
  }, [openModalParam, bcpId, isEditMode]);

  // Form handlers using new form state
  const handleInputChange = (field: keyof BCPFormData) => (value: string) => {
    formState.updateField(field, value);
  };

  // AI相談ボタンのクリック処理
  const handleAIConsult = (fieldId: string) => {
    setSelectedFieldId(fieldId);
  };

  // AIアシスタントの内容を適用
  const handleAIContentApply = (content: string) => {
    if (selectedFieldId) {
      formState.updateField(selectedFieldId as keyof BCPFormData, content);
    }
    setSelectedFieldId(null);
  };

  // Save handler using new actions
  const handleSave = async () => {
    // 保存中または保存済みの場合はスキップ
    if (isSavingOrDownloading || hasSaved) {
      return;
    }

    if (!formState.validateAll()) {
      return;
    }
    setIsSavingOrDownloading(true);
    try {
      const savedId = await actions.onSave(formState.formData);
      if (savedId && !isEditMode && !bcpId) {
        setSavedBcpId(savedId);
        setHasSaved(true); // 保存成功後は離脱確認不要
        // Navigate to edit mode URL (replace history to prevent regeneration on back/forward)
        const params = new URLSearchParams();
        params.set('bcpId', savedId);
        if (tenantId) {
          params.set('tenantId', tenantId);
        }
        navigate(`/create-bcp?${params.toString()}`, { replace: true });
      } else if (savedId) {
        // 既存BCPの更新時も保存成功とみなす
        setHasSaved(true);
      }
    } finally {
      setIsSavingOrDownloading(false);
    }
  };

  // Export handler using new actions - PDF出力のみ
  const handleExportPdf = async () => {
    // 保存中の場合はスキップ
    if (isSavingOrDownloading) {
      return;
    }

    // BCPが保存されていない場合は先に保存する
    if (!bcpId && !savedBcpId) {
      // バリデーションチェック
      if (!formState.validateAll()) {
        return;
      }

      setIsSavingOrDownloading(true);
      try {
        const savedId = await actions.onSave(formState.formData);
        if (savedId && !isEditMode && !bcpId) {
          setSavedBcpId(savedId);
          setHasSaved(true); // 保存成功後は離脱確認不要
          // Navigate to edit mode URL (replace history to prevent regeneration on back/forward)
          const params = new URLSearchParams();
          params.set('bcpId', savedId);
          if (tenantId) {
            params.set('tenantId', tenantId);
          }
          navigate(`/create-bcp?${params.toString()}`, { replace: true });
        } else if (savedId) {
          // 既存BCPの更新時も保存成功とみなす
          setHasSaved(true);
        }
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('BCP保存エラー:', error);
        return;
      } finally {
        setIsSavingOrDownloading(false);
      }
    }
    // 保存後または既に保存済みの場合はモーダルを開く
    setCurrentStep(3);
    setIsDownloadModalOpen(true);
  };

  // Show loading state
  if ((generationLoading && !isEditMode) || bcpLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <Spinner size="lg" />
          </div>
          <h2 className="text-xl font-medium text-gray-900 mb-2">
            {isEditMode ? 'BCPを読み込み中...' : 'AIがBCPを生成中...'}
          </h2>
          <p className="text-gray-600">
            {isEditMode 
              ? '保存されたBCPデータを読み込んでいます。'
              : 'アップロードされた資料を分析して、貴社の事業内容に応じたBCPの下書きを作成しています。'
            }
          </p>
          {!isEditMode && (
            <p className="text-gray-600">数十秒程度かかることがあります。</p>
          )}
        </div>
      </div>
    );
  }

  // fieldContextの構築
  const fieldContext = selectedFieldId
    ? {
        fieldId: selectedFieldId,
        fieldLabel: BCP_SECTIONS.find(s => s.id === selectedFieldId)?.title,
        currentValue: formState.getFieldValue(selectedFieldId),
        allFormData: formState.formData as Record<string, unknown>,
      }
    : undefined;

  return (
    <>
      <BCPFormLayout
      currentStep={currentStep}
      showHeaderActions={true}
      isEditMode={isEditMode}
      onSave={handleSave}
      onDownload={handleExportPdf}
      saveLoading={actions.saveLoading || isSavingOrDownloading}
      downloadLoading={actions.exportLoading || isSavingOrDownloading}
      saveDisabled={isSavingOrDownloading || hasSaved}
      downloadDisabled={isSavingOrDownloading}
      fieldContext={fieldContext}
      onAIContentApply={handleAIContentApply}
    >
      {/* ページタイトル */}
      <div className="bg-white p-8" style={{ borderRadius: '8px' }}>
        <BCPPageTitle
          title="簡易版BCPシート"
          isEditMode={isEditMode}
          editInfo={
            bcpDocument
              ? {
                  title: bcpDocument.content.title,
                  id: bcpDocument.id,
                }
              : undefined
          }
        />

        {/* GuideTopAI */}
        <div className="mb-6 flex justify-center">
          <GuideTopAI />
        </div>
      </div>

      {/* 成功メッセージ */}
      {actions.successMessage && (
        <Alert
          type="success"
          message={actions.successMessage}
          className="mb-6"
        />
      )}

      {/* エラー表示 */}
      {formState.errors.length > 0 && (
        <Alert
          type="error"
          title="エラーが発生しました"
          messages={formState.errors.map(error => error.message)}
          className="mb-6"
        />
      )}

      {/* BCPフォーム */}
      <div className="space-y-4 mt-8">
        {BCP_SECTION_GROUPS.map((group) => (
          <div
            key={group.id}
            className="bg-white rounded-lg shadow-sm"
          >
            <div className="p-6">
              {/* セクションヘッダー(大項目) */}
              <SectionHeader title={group.title} />

              {/* セクション本体(各フィールド) */}
              {group.fields.map((field) => (
                <div key={field.id}>
                  <AISuggestOffFormTextarea
                    label={field.title}
                    value={formState.getFieldValue(field.id)}
                    onChange={handleInputChange(field.id)}
                    placeholder={field.placeholder}
                    showAIButton={AI_CONSULT_FIELD_IDS.includes(field.id)}
                    onAIConsult={() => handleAIConsult(field.id)}
                    minHeight={getTextareaMinHeight(field.minHeight)}
                    highlightPlaceholders={field.id === 'rolesResponsibilities'}
                    readOnly={field.readOnly}
                  />

                  {/* ガイドセクション */}
                  {field.guide && (
                    <div style={{ marginTop: '24px' }}>
                      {field.guide.content && field.guide.example ? (
                        <BCPGuideSection
                          guideContent={field.guide.content}
                          exampleContent={field.guide.example}
                        />
                      ) : (
                        <BCPGuideSection>
                          {field.guide.content ?? field.guide.example}
                        </BCPGuideSection>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* アップロードファイル情報 */}
      <ReferenceMaterialsPanel
        uploadedFiles={uploadedFiles}
        className="mt-6"
      />

      {/* 次へボタン */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={handleExportPdf}
          disabled={isSavingOrDownloading}
          style={{
            backgroundColor: isSavingOrDownloading ? '#CCCCCC' : '#E60012',
            color: 'white',
            padding: '12px 32px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '20px',
            fontWeight: 500,
            fontFamily: 'Noto Sans JP, sans-serif',
            lineHeight: '20px',
            cursor: isSavingOrDownloading ? 'not-allowed' : 'pointer',
            boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)',
          }}
        >
          完成・ダウンロード
        </button>
      </div>
      </BCPFormLayout>

      {/* Download Modal */}
      <BCPDownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => {
          setIsDownloadModalOpen(false);
          setCurrentStep(2);
        }}
        bcpType={(isEditMode && bcpDocument ? bcpDocument.type : bcpType) as 'introductory' | 'simple' | 'basic'}
        bcpId={bcpId || savedBcpId}
      />
    </>
  );
};

export default BCPCreatePage;
